import React, { Fragment, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { Col, Row, Typography, Descriptions, Image, Tag, Button } from "antd";
import moment from "moment";
import PageTitle from "../page-header/PageHeader";
import Loader from "../loader/loader";
import baseUrl from "../../utils/baseUrl";
import {
  loadSingleProject,
  clearProject,
} from "../../redux/rtk/features/projectManagement/project/project/project";

const ViewProject = ({ drawer }) => {
  const projectId = useParams("id").projectId;
  const { Title } = Typography;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { loading, project } = useSelector((state) => state.project);

  useEffect(() => {
    dispatch(loadSingleProject(projectId));
    return () => {
      dispatch(clearProject());
    };
  }, [dispatch, projectId]);

  if (loading || !project) {
    return <Loader />;
  }

  return (
    <Fragment>
      <PageTitle title={"Back"} />
      <Row className="mr-top" justify={drawer ? "center" : "center"}>
        <Col
          xs={24}
          sm={24}
          md={24}
          lg={drawer ? 22 : 18}
          xl={drawer ? 22 : 16}
          className="column-design border rounded card-custom"
        >
          <Title level={3} className="m-2 mt-5 mb-5 text-center">
            Booking Details
          </Title>

          <Descriptions
            bordered
            size="middle"
            column={1}
            layout="horizontal"
            labelStyle={{ fontWeight: "bold", width: "30%" }}
            contentStyle={{ background: "#fafafa" }}
          >
            {/* Customer = requester */}
            <Descriptions.Item label="Customer">
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                {/* Show Customer Name */}
                <span>
                  {project.requester
                    ? `${project.requester.firstName} ${project.requester.lastName}`
                    : "-"}
                </span>

                {/* Show Customer Image */}
                {project.requester?.image ? (
                  <Image
                    src={`${baseUrl}/files/${project.requester.image}`}
                    width={50}
                    height={50}
                    style={{
                      objectFit: "cover",
                      borderRadius: "50%",
                      marginLeft: "5px",
                    }}
                    preview={{
                      mask: <span style={{ fontSize: 12 }}>View</span>,
                    }}
                  />
                ) : (
                  <Tag color="default">No Image</Tag>
                )}
              </div>
            </Descriptions.Item>

            <Descriptions.Item label="Customer Phone">
              {project.requester.phone || "-"}
            </Descriptions.Item>

            <Descriptions.Item label="Location">
              {project.location || "-"}
            </Descriptions.Item>

            {/* Service Man = projectManager */}
            <Descriptions.Item label="Service Man">
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                {/* Show Service Man Name */}
                <span>
                  {project.projectManager
                    ? `${project.projectManager.firstName} ${project.projectManager.lastName}`
                    : "-"}
                </span>

                {/* Show Service Man Image */}
                {project.projectManager?.image ? (
                  <Image
                    src={`${baseUrl}/files/${project.projectManager.image}`}
                    width={50}
                    height={50}
                    style={{
                      objectFit: "cover",
                      borderRadius: "50%",
                      marginLeft: "5px",
                    }}
                    preview={{
                      mask: <span style={{ fontSize: 12 }}>View</span>,
                    }}
                  />
                ) : (
                  <Tag color="default">No Image</Tag>
                )}
              </div>
            </Descriptions.Item>
            <Descriptions.Item label="Rating">
              <div className="flex items-center">
                <div className="flex">
                  {[...Array(5)].map((_, index) => {
                    const rating = Math.round(project.projectManager.rating || 0);
                    return (
                      <span
                        key={index}
                        style={{
                          fontSize: "18px",
                          lineHeight: "1",
                          marginRight: "2px",
                          color: index < rating ? "#FFD700" : "#d1d5db"
                        }}
                      >
                        ★
                      </span>
                    );
                  })}
                </div>
                <span className="ml-2">
                  {project.projectManager.rating || "0"}
                </span>
              </div>
            </Descriptions.Item>
            <Descriptions.Item label="Service Man Phone">
              {project.projectManager.phone || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Hourly Rate">
              {project.projectManager?.hourlyRate
                ? `€${project.projectManager.hourlyRate}`
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Task Name">
              {project.name || "-"}
            </Descriptions.Item>

            <Descriptions.Item label="Task Description">
              {project.description || "-"}
            </Descriptions.Item>

            <Descriptions.Item label="Task Image">
              {project.image ? (
                <Image
                  src={`${baseUrl}/files/${project.image}`}
                  width={100}
                  height={100}
                  style={{ objectFit: "cover", borderRadius: "12px" }}
                  preview={{
                    mask: <span style={{ fontSize: 14 }}>View</span>,
                  }}
                />
              ) : (
                <Tag color="default">No Image</Tag>
              )}
            </Descriptions.Item>

            <Descriptions.Item label="Start Date">
              {project.startDate
                ? moment(project.startDate).format("DD MMM YYYY")
                : "-"}
            </Descriptions.Item>

            <Descriptions.Item label="Start Time">
              {project.startTime
                ? moment(project.startTime, "HH:mm").format("hh:mm A")
                : "-"}
            </Descriptions.Item>

            <Descriptions.Item label="Custom Offer">
              {project.isCustomOffer ? (
                <Tag color="orange">Yes</Tag>
              ) : (
                <Tag color="red">No</Tag>
              )}
            </Descriptions.Item>

            <Descriptions.Item label="Hourly Rate From Customer">
              {project.offerValue ? `€${project.offerValue}` : "-"}
            </Descriptions.Item>

            <Descriptions.Item label="Booking Status">
              <Tag color={project.status === "COMPLETE" ? "green" : "blue"}>
                {project.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Total Worked Hours">
              {project.totalWorkedHours ? `${project.totalWorkedHours}` : "-"}

            </Descriptions.Item>
            <Descriptions.Item label="Agreed Negotiation">
              {project?.negotiationValue
                ? `€${Number(project.negotiationValue).toLocaleString()}`
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Total Payment">
              {project.totalPayment ? `€${project.totalPayment}` : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Service Man Earning">
              {project.serviceManEarning ? `€${project.serviceManEarning}` : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Admin Earning">
              {project.adminEarning ? `€${project.adminEarning}` : "-"}
            </Descriptions.Item>
          </Descriptions>

          <div style={{ marginTop: 25, marginBottom: 20, textAlign: "center" }}>
            <Button type="primary" onClick={() => navigate(-1)}>
              Back
            </Button>
          </div>
        </Col>
      </Row>
    </Fragment>
  );
};

export default ViewProject;
